import { forwardRef } from 'react';
import { alpha, Drawer, DrawerProps, SxProps, Theme, useTheme } from '@mui/material';

export interface CDrawerProps extends Omit<DrawerProps, 'PaperProps'> {
  width?:
    | number
    | string
    | {
        xs?: number | string;
        sm?: number | string;
        md?: number | string;
        lg?: number | string;
        xl?: number | string;
      };
  enableShadow?: boolean;
  enableBlur?: boolean;
  paperProps?: DrawerProps['PaperProps'];
}

export const CDrawer = forwardRef<HTMLDivElement, CDrawerProps>(
  (
    {
      width,
      enableShadow = true,
      enableBlur = false,
      anchor = 'right',
      paperProps,
      children,
      sx,
      ...other
    },
    ref,
  ) => {
    const theme = useTheme();

    // anchor 방향에 따른 그림자 생성
    const getDrawerShadow = () => {
      if (!enableShadow) return 'none';

      const isLight = theme.palette.mode === 'light';
      const shadowColor = alpha(
        isLight ? theme.palette.grey[500] : theme.palette.common.black,
        0.16,
      );

      switch (anchor) {
        case 'right':
          return `-24px 12px 40px 0 ${shadowColor}`;
        case 'left':
          return `24px 12px 40px 0 ${shadowColor}`;
        case 'top':
          return `0 24px 40px 0 ${shadowColor}`;
        case 'bottom':
          return `0 -24px 40px 0 ${shadowColor}`;
        default:
          return `-24px 12px 40px 0 ${shadowColor}`;
      }
    };

    // PaperProps sx 스타일 생성
    const paperSx: SxProps<Theme> = {
      ...(width && { width }),
      ...(enableShadow && { boxShadow: getDrawerShadow() }),
      ...(enableBlur && {
        backdropFilter: 'blur(8px)',
        backgroundColor: alpha(theme.palette.background.default, 0.9),
      }),
      ...paperProps?.sx,
    };

    return (
      <Drawer
        ref={ref}
        anchor={anchor}
        sx={sx}
        PaperProps={{
          ...paperProps,
          sx: paperSx,
        }}
        {...other}
      >
        {children}
      </Drawer>
    );
  },
);

CDrawer.displayName = 'CDrawer';

export default CDrawer;
